import {
  APIFieldAttributes,
  WebSocketRecalculationDonePayload,
  WebSocketRecalculationErrorPayload,
  WebSocketRecalculationStartedPayload,
} from '@ardoq/api-types';
import {
  APIErrorWithTraceId,
  CalculatedFieldEventStatus,
  CalculatedFieldEventMap,
  CalculatedFieldEventWithErrorMap,
  CalculatedFieldEvent,
} from './types';
import { ArdoqEvent } from 'sync/types';
import { t, type I18nLocale } from '@ardoq/i18n';

const isCalculatedFieldError = (
  possibleError: unknown
): possibleError is APIErrorWithTraceId =>
  possibleError !== null &&
  typeof possibleError === 'object' &&
  'traceId' in possibleError &&
  'message' in possibleError;

export const getCalculatedFieldErrorEvents = (
  events: CalculatedFieldEventMap
) =>
  Object.fromEntries(
    Object.entries(events).filter(([_, event]) =>
      isCalculatedFieldError(event.status)
    )
  ) as CalculatedFieldEventWithErrorMap;

const getCalculatedFieldCalculatingEventsFromUser = (
  events: CalculatedFieldEventMap,
  userId: string
) =>
  Object.fromEntries(
    Object.entries(events).filter(
      ([_, { status, userId: eventUserId }]) =>
        status === CalculatedFieldEventStatus.CALCULATING &&
        userId === eventUserId
    )
  );

export const getCalculatedFieldDoneEventsFromUser = (
  events: CalculatedFieldEventMap,
  userId: string
) =>
  Object.fromEntries(
    Object.entries(events).filter(
      ([_, { status, userId: eventUserId }]) =>
        status === CalculatedFieldEventStatus.DONE && userId === eventUserId
    )
  );

/**
 * If there are other fields being calculated, wait until the last one has finished,
 * and display a single toast.
 */
export const getCalculatedFieldDoneEventToastMessage = (
  userId: string,
  fieldId: string,
  calculatedFieldEvents: CalculatedFieldEventMap,
  calculatedFields: APIFieldAttributes[],
  i18nLocale: I18nLocale
) => {
  const calculatingEvents = getCalculatedFieldCalculatingEventsFromUser(
    calculatedFieldEvents,
    userId
  );
  const doneEvents = getCalculatedFieldDoneEventsFromUser(
    calculatedFieldEvents,
    userId
  );

  if (Object.keys(calculatingEvents).length > 0) {
    return;
  } else if (Object.keys(doneEvents).length > 1) {
    return t(
      i18nLocale,
      'menus:calculatedFields.toast.multipleRecalculated',
      { count: Object.keys(doneEvents).length },
      '{{count}} fields recalculated.'
    );
  }
  return t(
    i18nLocale,
    'menus:calculatedFields.toast.singleRecalculated',
    {
      label: calculatedFields.find(field => field._id === fieldId)?.label ?? '',
    },
    '{{label}} recalculated.'
  );
};

export const toRecalculationStartedEventPayload = (
  event: ArdoqEvent<WebSocketRecalculationStartedPayload>
): CalculatedFieldEvent => ({
  userId: event.user?.id,
  fieldId: event.data.id,
  status: CalculatedFieldEventStatus.CALCULATING,
});

export const toRecalculationDoneEventPayload = (
  event: ArdoqEvent<WebSocketRecalculationDonePayload>
): CalculatedFieldEvent => ({
  userId: event.user?.id,
  fieldId: event.data.id,
  status: CalculatedFieldEventStatus.DONE,
});

export const toRecalculationErrorEventPayload = (
  event: ArdoqEvent<WebSocketRecalculationErrorPayload>
): CalculatedFieldEvent => ({
  userId: event.user?.id,
  fieldId: event.data.id,
  status: { traceId: event.data.traceId, message: event.data.message },
});
